import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import { MoreVert as MoreVertIcon, Add as AddIcon, Edit as EditIcon, LockReset as LockResetIcon, Delete as DeleteIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAllUsers, updateUserRole, createUser, updateUser, resetUserPassword, deleteUser, resetUserMFA, User, CreateUserData } from '../../services/api';
import { format } from 'date-fns';
import { GenderField, USTALevelField } from '../forms';

const UserManagement = () => {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetMFADialogOpen, setResetMFADialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    gender: '',
    usta_level: '',
    role: 'player',
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: fetchAllUsers,
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSuccess('User role updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      handleMenuClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update user role');
      setTimeout(() => setError(null), 5000);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (userData: CreateUserData) => createUser(userData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSuccess(`User created successfully. Temporary password: ${data.temporary_password}`);
      setCreateDialogOpen(false);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create user');
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: { name?: string; gender?: string; usta_level?: string } }) =>
      updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to update user');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) => resetUserPassword(userId),
    onSuccess: (data) => {
      setSuccess(`Password reset email sent to ${data.email}. Link expires in ${data.expires_in_minutes} minutes.`);
      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to send reset email');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      setSuccess('User deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.error || 'Failed to delete user';
      const tournaments = err.response?.data?.tournaments;
      if (tournaments && Array.isArray(tournaments)) {
        setError(`${errorMessage}: ${tournaments.join(', ')}`);
      } else {
        setError(errorMessage);
      }
    },
  });

  const resetMFAMutation = useMutation({
    mutationFn: (userId: string) => resetUserMFA(userId),
    onSuccess: () => {
      setSuccess('MFA reset successfully!');
      setResetMFADialogOpen(false);
      setSelectedUser(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to reset MFA');
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleRoleChange = (newRole: string) => {
    if (selectedUser) {
      updateRoleMutation.mutate({ userId: selectedUser.id, role: newRole });
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'tournament_manager':
        return 'warning';
      case 'player':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'tournament_manager':
        return 'Tournament Manager';
      case 'player':
        return 'Player';
      default:
        return role;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleCreateUser = () => {
    setFormData({
      email: '',
      name: '',
      gender: '',
      usta_level: '',
      role: 'player',
    });
    setCreateDialogOpen(true);
  };

  const handleEditUser = () => {
    if (selectedUser) {
      setFormData({
        email: selectedUser.email,
        name: selectedUser.name,
        gender: selectedUser.gender || '',
        usta_level: selectedUser.usta_level || '',
        role: selectedUser.role,
      });
      setEditDialogOpen(true);
      setAnchorEl(null); // Close menu but keep selectedUser
    }
  };

  const handleResetPassword = () => {
    setResetPasswordDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedUser
  };

  const handleResetMFA = () => {
    setResetMFADialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedUser
  };

  const handleResetMFAConfirm = () => {
    if (selectedUser) {
      resetMFAMutation.mutate(selectedUser.id);
    }
  };

  const handleDeleteUser = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedUser
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  const handleCreateSubmit = () => {
    createUserMutation.mutate({
      email: formData.email,
      name: formData.name,
      gender: formData.gender,
      usta_level: formData.usta_level,
      role: formData.role,
    });
  };

  const handleEditSubmit = () => {
    if (!selectedUser) return;
    
    updateUserMutation.mutate({
      userId: selectedUser.id,
      userData: {
        name: formData.name,
        gender: formData.gender,
        usta_level: formData.usta_level,
      },
    });
  };

  const handleResetPasswordSubmit = () => {
    if (!selectedUser) return;
    resetPasswordMutation.mutate(selectedUser.id);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Create User
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>USTA Level</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.gender || '-'}</TableCell>
                <TableCell>{user.usta_level || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleLabel(user.role)}
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {format(new Date(user.created_at), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, user)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditUser}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <LockResetIcon fontSize="small" sx={{ mr: 1 }} />
          Reset Password
        </MenuItem>
        <MenuItem disabled>
          <strong>Change Role</strong>
        </MenuItem>
        <MenuItem
          onClick={() => handleRoleChange('player')}
          disabled={selectedUser?.role === 'player'}
        >
          Player
        </MenuItem>
        <MenuItem
          onClick={() => handleRoleChange('tournament_manager')}
          disabled={selectedUser?.role === 'tournament_manager'}
        >
          Tournament Manager
        </MenuItem>
        <MenuItem
          onClick={() => handleRoleChange('admin')}
          disabled={selectedUser?.role === 'admin'}
        >
          Admin
        </MenuItem>
        <MenuItem onClick={handleResetMFA}>
          <SecurityIcon fontSize="small" sx={{ mr: 1 }} />
          Reset MFA
        </MenuItem>
        <MenuItem onClick={handleDeleteUser} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenderField
                value={formData.gender}
                onChange={(value) => setFormData({ ...formData, gender: value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <USTALevelField
                value={formData.usta_level}
                onChange={(value) => setFormData({ ...formData, usta_level: value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              >
                <MenuItem value="player">Player</MenuItem>
                <MenuItem value="tournament_manager">Tournament Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                A temporary password will be generated and sent to the user's email. They will be required to change it on first login.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setSelectedUser(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenderField
                value={formData.gender}
                onChange={(value) => setFormData({ ...formData, gender: value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <USTALevelField
                value={formData.usta_level}
                onChange={(value) => setFormData({ ...formData, usta_level: value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedUser(null); }}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            A secure reset link will be emailed to <strong>{selectedUser?.email}</strong>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • Link expires in 10 minutes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • Link can only be used once
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • User will set their own password
          </Typography>
          <Alert severity="success" variant="outlined">
            <strong>Zero-Knowledge Security:</strong> You will never see the user's password.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleResetPasswordSubmit} 
            variant="contained" 
            disabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset MFA Dialog */}
      <Dialog open={resetMFADialogOpen} onClose={() => setResetMFADialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset User MFA</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            Are you sure you want to reset MFA for <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • This will disable MFA for the user
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • All MFA settings and backup codes will be cleared
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • User will need to set up MFA again if they want to use it
          </Typography>
          <Alert severity="info" variant="outlined">
            <strong>Use Case:</strong> This is useful when a user loses their MFA device and cannot access their account.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetMFADialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleResetMFAConfirm} 
            variant="contained" 
            color="warning"
            disabled={resetMFAMutation.isPending}
          >
            {resetMFAMutation.isPending ? 'Resetting...' : 'Reset MFA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • This action cannot be undone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            • User will be removed from Cognito and DynamoDB
          </Typography>
          <Typography variant="body2" color="error" sx={{ mb: 2, fontWeight: 'bold' }}>
            • User cannot be deleted if they are registered in any active or pending tournaments
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {users?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No users found
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UserManagement;
